import { AssetTypeId, SystemTypeId } from '@Types/assetSchema.types';
import { ChoiceListPairs } from '@Utils/choiceListFilesUtils';
import { RECORD_TYPE } from '@Utils/choiceListMapping';
import { getRecordTypeOptions } from '@Utils/recordTypeOptionsUtils';
import { useAssetSchema } from '../../hooks/assetSchemaHooks';
import { useGlobalization, useIsFeatureEnabled } from '@innovyze/stylovyze';

interface UseRecordTypeOptionsArgs {
	systemType: SystemTypeId;
	assetType: AssetTypeId;
}

export function useRecordTypeOptions({
	systemType,
	assetType,
}: UseRecordTypeOptionsArgs): {
	enabledRecordTypes: RECORD_TYPE[];
	recordTypeOptionsFlagged: ChoiceListPairs[];
} {
	const { t } = useGlobalization();

	const deteriorationModelFlag = useIsFeatureEnabled(
		'info360-rehab-enable-deterioration-model-results',
	) as boolean;

	const { data: schema } = useAssetSchema(systemType.id, assetType.id);

	const recordTypeOptionsPairDefinitions: ChoiceListPairs[] = [
		{ label: t('Asset attributes'), value: RECORD_TYPE.ASSET_ATTRIBUTES },
		{ label: t('Custom Table'), value: RECORD_TYPE.USER_DEFINED_FIELDS },
		{ label: t('Task'), value: RECORD_TYPE.TASK },
		{ label: t('Event'), value: RECORD_TYPE.EVENT },
		{ label: t('Action'), value: RECORD_TYPE.ACTION },
		{ label: t('Condition'), value: RECORD_TYPE.CONDITION },
		{ label: t('Inspection'), value: RECORD_TYPE.INSPECTION },
		{ label: t('Risk'), value: RECORD_TYPE.RISK },
		{
			label: t('Deterioration Model'),
			value: RECORD_TYPE.DETERIORATION_MODEL,
		},
		{
			label: t('Failure Definition'),
			value: RECORD_TYPE.FAILURE_DEFINITION,
		},
	];

	const recordTypeOptions = schema ? getRecordTypeOptions(schema) : [];

	// Filter out DETERIORATION_MODEL and FAILURE_DEFINITION if feature flag is not enabled
	const filteredRecordTypeOptions = deteriorationModelFlag
		? recordTypeOptions
		: recordTypeOptions.filter(
				record =>
					record !== RECORD_TYPE.DETERIORATION_MODEL &&
					record !== RECORD_TYPE.FAILURE_DEFINITION,
			);

	const recordTypeOptionsPairs = filteredRecordTypeOptions
		.map(record => {
			return recordTypeOptionsPairDefinitions.find(
				option => option.value === record,
			);
		})
		.filter((option): option is ChoiceListPairs => option !== undefined);

	return {
		enabledRecordTypes: filteredRecordTypeOptions,
		recordTypeOptionsFlagged: recordTypeOptionsPairs,
	};
}
